import { useEffect, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Church,
  Loader2,
  Settings2,
  ShieldCheck,
  UserRound,
  X,
} from "lucide-react";

import { PlatformStatusBadge } from "@/components/platform/ui/PlatformStatusBadge";
import {
  provisionPilotChurch,
  type PilotRequest,
} from "@/api/platform";

type ProvisionChurchDrawerProps = {
  open: boolean;
  request: PilotRequest | null;
  onClose: () => void;
  onComplete: (requestId: string) => void;
};

type ProvisionStep =
  | "CHURCH"
  | "ADMINISTRATOR"
  | "PILOT"
  | "REVIEW";

type ProvisionForm = {
  churchName: string;
  slug: string;
  timezone: string;

  adminFirstName: string;
  adminLastName: string;
  adminEmail: string;

  maxUsers: string;
  maxMembers: string;
  pilotEndDate: string;

  migrationEnabled: boolean;
  reportingEnabled: boolean;
  careEnabled: boolean;
  communicationEnabled: boolean;
  intelligenceCenterEnabled: boolean;
};

const steps: Array<{
  code: ProvisionStep;
  label: string;
  icon: typeof Church;
}> = [
  {
    code: "CHURCH",
    label: "Church",
    icon: Church,
  },
  {
    code: "ADMINISTRATOR",
    label: "Administrator",
    icon: UserRound,
  },
  {
    code: "PILOT",
    label: "Pilot Settings",
    icon: Settings2,
  },
  {
    code: "REVIEW",
    label: "Review",
    icon: ShieldCheck,
  },
];

export function ProvisionChurchDrawer({
  open,
  request,
  onClose,
  onComplete,
}: ProvisionChurchDrawerProps) {
  const [step, setStep] = useState<ProvisionStep>("CHURCH");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState<ProvisionForm>(() =>
    createInitialForm(request),
  );

  useEffect(() => {
    if (!open || !request) {
      return;
    }

    setStep("CHURCH");
    setForm(createInitialForm(request));
    setIsSubmitting(false);
  }, [open, request]);

  useEffect(() => {
    if (!open) {
      return;
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape" && !isSubmitting) {
        onClose();
      }
    }

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, isSubmitting, onClose]);

  if (!open || !request) {
    return null;
  }

  const currentStepIndex = steps.findIndex(
    (item) => item.code === step,
  );

  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === steps.length - 1;

  function updateField<K extends keyof ProvisionForm>(
    field: K,
    value: ProvisionForm[K],
  ) {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  }

  function goBack() {
    if (isFirstStep) {
      return;
    }

    setStep(steps[currentStepIndex - 1].code);
  }

  function goNext() {
    if (isLastStep) {
      return;
    }

    setStep(steps[currentStepIndex + 1].code);
  }

  async function handleCreateWorkspace() {
    setIsSubmitting(true);

    try {      

      await provisionPilotChurch(request.id, {
        church_name: form.churchName.trim(),
        slug: form.slug.trim(),
        timezone: form.timezone,

        admin_first_name: form.adminFirstName.trim(),
        admin_last_name: form.adminLastName.trim(),
        admin_email: form.adminEmail.trim(),

        max_users: Number(form.maxUsers),
        max_member_records: Number(form.maxMembers),

        pilot_end_date: form.pilotEndDate,

        invitation_expires_in_days: 7,

        feature_flags: {
          migration: form.migrationEnabled,
          reporting: form.reportingEnabled,
          care: form.careEnabled,
          communication: form.communicationEnabled,
          intelligence_center_ui:
            form.intelligenceCenterEnabled,
        },
      });

      onComplete(request.id);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[70]">
      <button
        type="button"
        aria-label="Close provisioning drawer"
        onClick={isSubmitting ? undefined : onClose}
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
      />

      <aside className="absolute inset-y-0 right-0 flex w-full max-w-2xl flex-col bg-white shadow-2xl">
        <header className="border-b border-slate-200 px-6 py-5">
          <div className="flex items-start justify-between gap-5">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-indigo-600">
                Pilot provisioning
              </p>

              <h2 className="mt-2 text-2xl font-bold tracking-tight text-slate-950">
                Create church workspace
              </h2>

              <p className="mt-2 text-sm text-slate-500">
                Provision {request.church_name} and prepare the initial
                administrator invitation.
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 text-slate-500 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Close drawer"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="mt-6 grid grid-cols-4 gap-2">
            {steps.map((item, index) => {
              const Icon = item.icon;
              const complete = index < currentStepIndex;
              const active = item.code === step;

              return (
                <button
                  key={item.code}
                  type="button"
                  onClick={() => {
                    if (index <= currentStepIndex) {
                      setStep(item.code);
                    }
                  }}
                  className={[
                    "rounded-xl border p-3 text-left transition",
                    active
                      ? "border-indigo-300 bg-indigo-50"
                      : complete
                        ? "border-emerald-200 bg-emerald-50"
                        : "border-slate-200 bg-white",
                  ].join(" ")}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={[
                        "flex h-7 w-7 items-center justify-center rounded-lg",
                        active
                          ? "bg-indigo-600 text-white"
                          : complete
                            ? "bg-emerald-600 text-white"
                            : "bg-slate-100 text-slate-500",
                      ].join(" ")}
                    >
                      {complete ? (
                        <Check className="h-4 w-4" />
                      ) : (
                        <Icon className="h-4 w-4" />
                      )}
                    </span>

                    <span className="hidden text-xs font-semibold text-slate-700 sm:block">
                      {item.label}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
          {step === "CHURCH" && (
            <ChurchStep form={form} updateField={updateField} />
          )}

          {step === "ADMINISTRATOR" && (
            <AdministratorStep
              form={form}
              updateField={updateField}
            />
          )}

          {step === "PILOT" && (
            <PilotSettingsStep
              form={form}
              updateField={updateField}
            />
          )}

          {step === "REVIEW" && (
            <ReviewStep request={request} form={form} />
          )}
        </div>

        <footer className="flex items-center justify-between gap-4 border-t border-slate-200 bg-slate-50 px-6 py-4">
          <button
            type="button"
            onClick={goBack}
            disabled={isFirstStep || isSubmitting}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>

          {isLastStep ? (
            <button
              type="button"
              onClick={handleCreateWorkspace}
              disabled={isSubmitting}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Creating workspace
                </>
              ) : (
                <>
                  <ShieldCheck className="h-4 w-4" />
                  Create Workspace
                </>
              )}
            </button>
          ) : (
            <button
              type="button"
              onClick={goNext}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-indigo-500"
            >
              Continue
              <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </footer>
      </aside>
    </div>
  );
}

function ChurchStep({
  form,
  updateField,
}: StepProps) {
  return (
    <StepSection
      title="Church information"
      description="Confirm the church workspace identity and default location settings."
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Field
            label="Church name"
            value={form.churchName}
            onChange={(value) => {
              updateField("churchName", value);
              updateField("slug", createSlug(value));
            }}
          />
        </div>

        <Field
          label="Workspace slug"
          value={form.slug}
          prefix="app.ministryiq.app/"
          onChange={(value) =>
            updateField("slug", createSlug(value))
          }
        />

        <SelectField
          label="Timezone"
          value={form.timezone}
          onChange={(value) =>
            updateField("timezone", value)
          }
        >
          <option value="America/New_York">
            Eastern Time
          </option>
          <option value="America/Chicago">
            Central Time
          </option>
          <option value="America/Denver">
            Mountain Time
          </option>
          <option value="America/Los_Angeles">
            Pacific Time
          </option>
        </SelectField>
      </div>

      <Notice>
        The church will initially be created with a status of{" "}
        <strong>PENDING_ACTIVATION</strong>.
      </Notice>
    </StepSection>
  );
}

function AdministratorStep({
  form,
  updateField,
}: StepProps) {
  return (
    <StepSection
      title="Initial administrator"
      description="This person will receive the secure invitation to create the first church administrator account."
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <Field
          label="First name"
          value={form.adminFirstName}
          onChange={(value) =>
            updateField("adminFirstName", value)
          }
        />

        <Field
          label="Last name"
          value={form.adminLastName}
          onChange={(value) =>
            updateField("adminLastName", value)
          }
        />

        <div className="sm:col-span-2">
          <Field
            label="Administrator email"
            type="email"
            value={form.adminEmail}
            onChange={(value) =>
              updateField("adminEmail", value)
            }
          />
        </div>
      </div>

      <Notice>
        The invitation will be assigned to this email address and
        will expire after seven days.
      </Notice>
    </StepSection>
  );
}

function PilotSettingsStep({
  form,
  updateField,
}: StepProps) {
  return (
    <StepSection
      title="Pilot settings"
      description="Configure limits and enable the MinistryIQ modules available to this church."
    >
      <div className="grid gap-5 sm:grid-cols-3">
        <Field
          label="Maximum users"
          type="number"
          value={form.maxUsers}
          onChange={(value) =>
            updateField("maxUsers", value)
          }
        />

        <Field
          label="Maximum members"
          type="number"
          value={form.maxMembers}
          onChange={(value) =>
            updateField("maxMembers", value)
          }
        />

        <Field
          label="Pilot end date"
          type="date"
          value={form.pilotEndDate}
          onChange={(value) =>
            updateField("pilotEndDate", value)
          }
        />
      </div>

      <div className="mt-7">
        <p className="text-sm font-semibold text-slate-900">
          Enabled features
        </p>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <FeatureToggle
            label="Member migration"
            description="CSV preview, mapping, duplicate review, and import."
            checked={form.migrationEnabled}
            onChange={(checked) =>
              updateField("migrationEnabled", checked)
            }
          />

          <FeatureToggle
            label="Reporting"
            description="Attendance summary and reporting foundation."
            checked={form.reportingEnabled}
            onChange={(checked) =>
              updateField("reportingEnabled", checked)
            }
          />

          <FeatureToggle
            label="Care/Follow-up"
            description="Automatic member intelligence and follow-up queue."
            checked={form.careEnabled}
            onChange={(checked) =>
              updateField("careEnabled", checked)
            }
          />

          <FeatureToggle
            label="Communication"
            description="SMS and email communication tools."
            checked={form.communicationEnabled}
            onChange={(checked) =>
              updateField("communicationEnabled", checked)
            }
          />

          <FeatureToggle
            label="Intelligence Center UI"
            description="Dedicated intelligence review interface."
            checked={form.intelligenceCenterEnabled}
            onChange={(checked) =>
              updateField(
                "intelligenceCenterEnabled",
                checked,
              )
            }
          />
        </div>
      </div>
    </StepSection>
  );
}

function ReviewStep({
  request,
  form,
}: {
  request: PilotRequest;
  form: ProvisionForm;
}) {
  const features = [
    ["Migration", form.migrationEnabled],
    ["Reporting", form.reportingEnabled],
    ["Care/Follow-up", form.careEnabled],
    ["Communication", form.communicationEnabled],
    [
      "Intelligence Center UI",
      form.intelligenceCenterEnabled,
    ],
  ] as const;

  return (
    <StepSection
      title="Review workspace"
      description="Confirm the church, administrator, pilot limits, and enabled features before provisioning."
    >
      <div className="space-y-5">
        <ReviewCard title="Church">
          <ReviewItem label="Name" value={form.churchName} />
          <ReviewItem label="Slug" value={form.slug} />
          <ReviewItem label="Timezone" value={form.timezone} />
          <ReviewItem
            label="Request status"
            value={
              <PlatformStatusBadge status={request.status} />
            }
          />
        </ReviewCard>

        <ReviewCard title="Administrator">
          <ReviewItem
            label="Name"
            value={`${form.adminFirstName} ${form.adminLastName}`.trim()}
          />
          <ReviewItem label="Email" value={form.adminEmail} />
          <ReviewItem label="Role" value="Church Administrator" />
        </ReviewCard>

        <ReviewCard title="Pilot configuration">
          <ReviewItem
            label="Maximum users"
            value={form.maxUsers}
          />
          <ReviewItem
            label="Maximum members"
            value={form.maxMembers}
          />
          <ReviewItem
            label="Pilot end date"
            value={form.pilotEndDate}
          />
        </ReviewCard>

        <ReviewCard title="Features">
          <div className="flex flex-wrap gap-2">
            {features.map(([label, enabled]) => (
              <span
                key={label}
                className={[
                  "rounded-full px-3 py-1.5 text-xs font-semibold",
                  enabled
                    ? "bg-emerald-50 text-emerald-700"
                    : "bg-slate-100 text-slate-500",
                ].join(" ")}
              >
                {enabled ? "Enabled" : "Disabled"} · {label}
              </span>
            ))}
          </div>
        </ReviewCard>
      </div>
    </StepSection>
  );
}

type StepProps = {
  form: ProvisionForm;
  updateField: <K extends keyof ProvisionForm>(
    field: K,
    value: ProvisionForm[K],
  ) => void;
};

function StepSection({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h3 className="text-xl font-semibold text-slate-950">
        {title}
      </h3>

      <p className="mt-2 text-sm leading-6 text-slate-500">
        {description}
      </p>

      <div className="mt-7">{children}</div>
    </section>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  prefix,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  prefix?: string;
}) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium text-slate-700">
        {label}
      </span>

      <div className="flex overflow-hidden rounded-xl border border-slate-200 bg-white focus-within:border-indigo-400 focus-within:ring-4 focus-within:ring-indigo-100">
        {prefix && (
          <span className="flex items-center border-r border-slate-200 bg-slate-50 px-3 text-xs text-slate-500">
            {prefix}
          </span>
        )}

        <input
          type={type}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="h-11 min-w-0 flex-1 border-0 bg-transparent px-4 text-sm text-slate-900 outline-none"
        />
      </div>
    </label>
  );
}

function SelectField({
  label,
  value,
  onChange,
  children,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium text-slate-700">
        {label}
      </span>

      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-11 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm text-slate-900 outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100"
      >
        {children}
      </select>
    </label>
  );
}

function FeatureToggle({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-4 rounded-xl border border-slate-200 p-4 transition hover:bg-slate-50">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-1 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
      />

      <span>
        <span className="block text-sm font-semibold text-slate-900">
          {label}
        </span>

        <span className="mt-1 block text-xs leading-5 text-slate-500">
          {description}
        </span>
      </span>
    </label>
  );
}

function Notice({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <div className="mt-7 rounded-xl border border-indigo-200 bg-indigo-50 p-4 text-sm leading-6 text-indigo-800">
      {children}
    </div>
  );
}

function ReviewCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-slate-200 p-5">
      <p className="text-sm font-semibold text-slate-950">
        {title}
      </p>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        {children}
      </div>
    </div>
  );
}

function ReviewItem({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
        {label}
      </p>

      <div className="mt-2 text-sm text-slate-900">
        {value}
      </div>
    </div>
  );
}

function createInitialForm(
  request: PilotRequest | null,
): ProvisionForm {
  const contactName = getOptionalString(
    request,
    "contact_name",
  );

  const firstName = getOptionalString(
    request,
    "contact_first_name",
  );

  const lastName = getOptionalString(
    request,
    "contact_last_name",
  );

  const contactParts = contactName
    ? contactName.split(/\s+/)
    : [];

  const churchName = getOptionalString(
    request,
    "church_name",
  );

  const contactEmail = getOptionalString(
    request,
    "contact_email",
  );

  const churchSize =
    getOptionalString(request, "church_size_range") ||
    getOptionalString(request, "church_size");

  return {
    churchName,
    slug: createSlug(churchName),
    timezone: "America/New_York",

    adminFirstName:
      firstName || contactParts[0] || "",

    adminLastName:
      lastName || contactParts.slice(1).join(" "),

    adminEmail: contactEmail,

    maxUsers: "10",
    maxMembers: getSuggestedMemberLimit(churchSize),
    pilotEndDate: "2026-12-31",

    migrationEnabled: true,
    reportingEnabled: true,
    careEnabled: true,
    communicationEnabled: false,
    intelligenceCenterEnabled: false,
  };
}

function getOptionalString(
  value: unknown,
  key: string,
): string {
  if (!value || typeof value !== "object") {
    return "";
  }

  const fieldValue = (value as Record<string, unknown>)[key];

  return typeof fieldValue === "string"
    ? fieldValue.trim()
    : "";
}

function getSuggestedMemberLimit(
  churchSize?: string | null,
): string {
  if (!churchSize) {
    return "1000";
  }

  const numbers = churchSize.match(/\d[\d,]*/g);

  if (!numbers || numbers.length === 0) {
    return "1000";
  }

  const largestNumber = Math.max(
    ...numbers.map((value) =>
      Number(value.replaceAll(",", "")),
    ),
  );

  if (!Number.isFinite(largestNumber)) {
    return "1000";
  }

  return String(
    Math.max(
      1000,
      Math.ceil(largestNumber / 500) * 500,
    ),
  );
}

function createSlug(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}